<template>
  <v-container>
    <h1 class="mb-4">Admin Dashboard</h1>

  <div class="mainDisplayButtons" style="margin-bottom: 10px; display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 4px;">
    <v-btn small @click="tab = 'pending'"    class="mx-1" color="primary"   variant="outlined" :active="tab === 'pending'">Pending Requests</v-btn>
    <v-btn small @click="tab = 'active'"     class="mx-1" color="primary"   variant="outlined" :active="tab === 'active'">All Users</v-btn>
    <v-btn small @click="tab = 'orphan'"     class="mx-1" color="primary"   variant="outlined" :active="tab === 'orphan'">Orphan Sessions</v-btn>
    <v-btn small @click="goToOrphanedUsers"  class="mx-1" color="secondary" variant="outlined">Orphaned Users</v-btn>
    <v-btn small @click="tab = 'places'"     class="mx-1" color="primary"   variant="outlined" :active="tab === 'places'">Places</v-btn>
    <v-btn small @click="tab = 'games'"      class="mx-1" color="primary"   variant="outlined" :active="tab === 'games'">Games</v-btn>
    <v-btn small @click="tab = 'structures'" class="mx-1" color="primary"   variant="outlined" :active="tab === 'structures'">Structures</v-btn>
    <v-btn small @click="tab = 'missinguser'" class="mx-1" color="primary" variant="outlined" :active="tab === 'missinguser'">Missing User ID</v-btn>
    <v-btn small @click="tab = 'feedback'"  class="mx-1" color="primary"   variant="outlined" :active="tab === 'feedback'">
      Feedback
      <v-badge v-if="unreadFeedbackCount > 0" :content="unreadFeedbackCount" color="error" inline class="ml-1" />
    </v-btn>
   </div>

    <v-window v-model="tab">
      <!-- Pending User Approvals Tab -->
      <v-window-item value="pending">
        <v-card>
          <v-card-title>Pending Requests</v-card-title>
          <v-card-text>
            <v-list lines="three">
              <div v-if="pendingUsers.length === 0" class="text-center pa-4">
                No pending user requests.
              </div>
              <v-list-item
                v-for="user in pendingUsers"
                :key="user.id"
                :title="user.alias"
                :subtitle="`User ID: ${user.user_id}`"
              >
                <p>
                  Requests:
                  <v-chip v-if="user.permissions.poker === 'pending'" class="ma-1" color="blue" label>Poker Tracker</v-chip>
                  <v-chip v-if="user.permissions.tournament === 'pending'" class="ma-1" color="green" label>Tournament Timer</v-chip>
                </p>

                <template v-slot:append>
                  <v-btn
                    v-if="user.permissions.poker === 'pending'"
                    color="primary"
                    size="small"
                    class="mr-2"
                    @click="approve(user, 'poker')"
                  >Approve Poker</v-btn>
                  <v-btn
                    v-if="user.permissions.tournament === 'pending'"
                    color="success"
                    size="small"
                    @click="approve(user, 'tournament')"
                  >Approve Tournament</v-btn>
                </template>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-window-item>

      <!-- Orphan Sessions Tab -->
      <v-window-item value="orphan">
        <v-card>
          <v-card-title>Orphan Sessions (Not Attached to Active Users)</v-card-title>
          <v-card-text>
            <v-list lines="three">
              <div v-if="orphanSessions.length === 0" class="text-center pa-4">
                No orphan sessions found.
              </div>
              <v-list-item
                v-for="session in orphanSessions"
                :key="session.id"
              >
                <div>
                  <v-list-item-title>{{ session.from }} — {{ formatDate(session.sessiondate) }}</v-list-item-title>
                  <v-list-item-subtitle>{{ session.place }} • {{ session.game }} • {{ session.type }}</v-list-item-subtitle>
                </div>
                <template v-slot:append>
                  <v-btn color="red-darken-2" size="small" icon @click="requestDeleteSession(session)" title="Delete Session">
                    <v-icon>mdi-delete-forever</v-icon>
                  </v-btn>
                </template>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-window-item>

      <!-- All Users Tab (with permission checkboxes) -->
      <v-window-item value="active">
        <v-card>
          <v-card-title>All Users</v-card-title>
          <v-card-text>
            <div class="d-flex flex-wrap align-center justify-center gap-4 mb-4">
              <span class="text-body-2 text-medium-emphasis">Filter by permission:</span>
              <v-select
                v-model="filterPoker"
                :items="filterOptions"
                item-title="title"
                item-value="value"
                density="compact"
                label="Poker Tracker"
                hide-details
                style="max-width: 120px;"
              />
              <v-select
                v-model="filterTournament"
                :items="filterOptions"
                item-title="title"
                item-value="value"
                density="compact"
                label="Tournament Timer"
                hide-details
                style="max-width: 120px;"
              />
              <v-select
                v-model="filterAdmin"
                :items="filterOptions"
                item-title="title"
                item-value="value"
                density="compact"
                label="Admin"
                hide-details
                style="max-width: 120px;"
              />
            </div>
            <v-data-table
              :headers="userHeaders"
              :items="filteredUsers"
              :sort-by="[{ key: 'alias', order: 'asc' }]"
              density="compact"
              class="mt-2"
              no-data-text="No users match the filters."
            >
              <template #item.lastLogin="{ item }">
                {{ formatLastLogin(item.lastLogin) }}
              </template>
              <template #item.sessionCount="{ item }">
                <span v-if="item.sessionCount === null" class="text-medium-emphasis">…</span>
                <span v-else>{{ item.sessionCount }}</span>
              </template>
              <template #item.tournamentCount="{ item }">
                <span v-if="item.tournamentCount === null" class="text-medium-emphasis">…</span>
                <span v-else>{{ item.tournamentCount }}</span>
              </template>
              <template #item.permissions="{ item }">
                <div class="d-flex align-center" style="gap: 4px;">
                  <v-checkbox
                    :model-value="item.permissions.poker === 'approved'"
                    hide-details density="compact" color="blue"
                    label="Poker"
                    @update:model-value="(v) => setPermission(item, 'poker', v)"
                  />
                  <v-checkbox
                    :model-value="item.permissions.tournament === 'approved'"
                    hide-details density="compact" color="green"
                    label="Tourney"
                    @update:model-value="(v) => setPermission(item, 'tournament', v)"
                  />
                  <v-checkbox
                    :model-value="!!item.permissions.admin"
                    hide-details density="compact" color="orange"
                    label="Admin"
                    @update:model-value="(v) => setPermission(item, 'admin', v)"
                  />
                </div>
              </template>
              <template #item.actions="{ item }">
                <v-btn color="red-darken-2" size="small" icon @click="requestDeleteUser(item)" title="Delete User">
                  <v-icon>mdi-delete-forever</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-window-item>

      <!-- Places Tab -->
      <v-window-item value="places">
        <AdminPlacesPanel v-if="loadedTabs.has('places')" />
      </v-window-item>

      <!-- Games Tab -->
      <v-window-item value="games">
        <AdminGamesPanel v-if="loadedTabs.has('games')" />
      </v-window-item>

      <!-- Structures Tab -->
      <v-window-item value="structures">
        <AdminStructuresPanel v-if="loadedTabs.has('structures')" :allUsers="allUsers" />
      </v-window-item>

      <!-- Missing User ID Tab -->
      <v-window-item value="missinguser">
        <v-card>
          <v-card-title>Sessions Without User ID</v-card-title>
          <v-card-subtitle>These sessions have no user_id and won't appear in any user's history.</v-card-subtitle>
          <v-card-text>
            <div v-if="missingUserIdLoading" class="text-center pa-4">
              <v-progress-circular indeterminate color="primary" />
            </div>
            <div v-else-if="missingUserIdSessions.length === 0" class="text-center pa-4">
              No sessions with missing user IDs found.
            </div>
            <v-table v-else density="compact">
              <thead>
                <tr>
                  <th class="text-left">Date</th>
                  <th class="text-left">Place</th>
                  <th class="text-left">From</th>
                  <th class="text-left">Type</th>
                  <th class="text-left">Assign User</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="session in missingUserIdSessions" :key="session.id">
                  <td>{{ formatDate(session.sessiondate) }}</td>
                  <td>{{ session.place || '—' }}</td>
                  <td>{{ session.from || '—' }}</td>
                  <td>{{ session.type || '—' }}</td>
                  <td>
                    <v-select
                      v-model="sessionAssignments[session.id]"
                      :items="userOptions"
                      item-title="title"
                      item-value="value"
                      label="Select user"
                      density="compact"
                      hide-details
                      style="min-width: 200px;"
                    />
                  </td>
                  <td>
                    <v-btn
                      size="small"
                      color="primary"
                      :loading="savingSessionId === session.id"
                      :disabled="!sessionAssignments[session.id]"
                      @click="assignUserId(session)"
                    >Save</v-btn>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-window-item>

      <!-- Feedback Tab -->
      <v-window-item value="feedback">
        <v-card>
          <v-card-title>User Feedback</v-card-title>
          <v-card-text>
            <div v-if="allFeedback.length === 0" class="text-center pa-4">No feedback submitted yet.</div>
            <v-list v-else lines="three">
              <v-list-item
                v-for="item in allFeedback"
                :key="item.id"
                :style="item.status === 'New' ? 'background: rgba(var(--v-theme-primary), 0.06);' : ''"
              >
                <div>
                  <div class="d-flex align-center flex-wrap mb-1" style="gap: 8px;">
                    <v-chip size="small" :color="feedbackTypeColor(item.type)">{{ item.type }}</v-chip>
                    <strong>{{ item.subject }}</strong>
                    <span class="text-caption text-medium-emphasis">— {{ item.userAlias }}</span>
                    <span class="text-caption text-medium-emphasis">{{ formatDate(item.submittedAt) }}</span>
                  </div>
                  <div class="mb-2">{{ item.message }}</div>
                  <div v-if="item.userEmail" class="text-caption text-medium-emphasis">{{ item.userEmail }}</div>
                </div>
                <template v-slot:append>
                  <v-select
                    :model-value="item.status"
                    :items="feedbackStatuses"
                    density="compact"
                    style="width: 140px;"
                    hide-details
                    @update:model-value="val => updateFeedbackStatus(item, val)"
                  />
                </template>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-window-item>
    </v-window>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">Confirm User Deletion</v-card-title>
        <v-card-text>
          Are you sure you want to permanently delete the user <strong>{{ userToDelete?.alias }}</strong>? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" text @click="confirmDeleteUser">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Delete Session Confirmation Dialog -->
    <v-dialog v-model="sessionDeleteDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">Confirm Session Deletion</v-card-title>
        <v-card-text>
          Are you sure you want to permanently delete the session created by <strong>{{ sessionToDelete?.from }}</strong> on <strong>{{ formatDate(sessionToDelete?.sessiondate) }}</strong>? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="sessionDeleteDialog = false">Cancel</v-btn>
          <v-btn color="error" text @click="confirmDeleteSession">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { db } from '@/firebaseConfig';
import { collection, query, where, getDocs, getCountFromServer, doc, updateDoc, deleteDoc, orderBy } from 'firebase/firestore';
import {
  USERS_DB,
  SESSIONS_DB,
  TOURNAMENT_SETTINGS_DB,
  TOURNAMENT_PLAYERS_DB,
  TOURNAMENT_PAYOUTS_DB,
  TOURNAMENT_TIMERS_DB,
  SAVED_TOURNAMENT_RESULTS_DB,
  FEEDBACK_DB
} from '@/constants/constants';
import AdminPlacesPanel    from '@/components/admin/AdminPlacesPanel.vue';
import AdminGamesPanel     from '@/components/admin/AdminGamesPanel.vue';
import AdminStructuresPanel from '@/components/admin/AdminStructuresPanel.vue';

const tab = ref('pending');
const router = useRouter();
const pendingUsers = ref([]);
const allUsers = ref([]);
const deleteDialog = ref(false);
const userToDelete = ref(null);
const orphanSessions = ref([]);
const sessionDeleteDialog = ref(false);
const sessionToDelete = ref(null);
const missingUserIdSessions = ref([]);
const missingUserIdLoading = ref(false);
const sessionAssignments = ref({});
const savingSessionId = ref(null);
const allFeedback = ref([]);
const feedbackStatuses = ['New', 'In Progress', 'Resolved'];

const unreadFeedbackCount = computed(() => allFeedback.value.filter(f => f.status === 'New').length);

// Track which tabs have been mounted (for lazy loading)
const loadedTabs = ref(new Set(['pending']));

const filterPoker = ref('all');
const filterTournament = ref('all');
const filterAdmin = ref('all');
const filterOptions = [
  { title: 'All', value: 'all' },
  { title: 'On', value: 'on' },
  { title: 'Off', value: 'off' }
];

const userHeaders = [
  { title: 'Alias',       key: 'alias',           sortable: true  },
  { title: 'Last Login',  key: 'lastLogin',        sortable: true  },
  { title: 'Sessions',    key: 'sessionCount',     sortable: true  },
  { title: 'Tournaments', key: 'tournamentCount',  sortable: true  },
  { title: 'Permissions', key: 'permissions',      sortable: false },
  { title: '',            key: 'actions',          sortable: false },
];

const filteredUsers = computed(() => {
  return allUsers.value.filter(user => {
    const p = user.permissions || {};
    const pokerOn = p.poker === 'approved';
    const tournamentOn = p.tournament === 'approved';
    const adminOn = !!p.admin;
    if (filterPoker.value === 'on' && !pokerOn) return false;
    if (filterPoker.value === 'off' && pokerOn) return false;
    if (filterTournament.value === 'on' && !tournamentOn) return false;
    if (filterTournament.value === 'off' && tournamentOn) return false;
    if (filterAdmin.value === 'on' && !adminOn) return false;
    if (filterAdmin.value === 'off' && adminOn) return false;
    return true;
  });
});

const userOptions = computed(() =>
  allUsers.value
    .filter(u => u.user_id)
    .map(u => ({ title: `${u.alias || '(no alias)'} — ${u.user_id}`, value: u.user_id }))
    .sort((a, b) => a.title.localeCompare(b.title))
);

const defaultPermissions = () => ({
  poker: 'approved',
  tournament: null,
  admin: false
});

const fetchPendingUsers = async () => {
  const usersRef = collection(db, USERS_DB);
  const q = query(usersRef, where('permissions.admin', '==', false));
  const querySnapshot = await getDocs(q);
  pendingUsers.value = querySnapshot.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(user => user.permissions && (user.permissions.poker === 'pending' || user.permissions.tournament === 'pending'));
};

const fetchAllUsers = async () => {
  const usersRef = collection(db, USERS_DB);
  const querySnapshot = await getDocs(usersRef);
  const list = [];
  for (const docSnap of querySnapshot.docs) {
    const data = docSnap.data();
    const id = docSnap.id;
    let permissions = data.permissions;
    if (!permissions || typeof permissions !== 'object') {
      permissions = defaultPermissions();
      await updateDoc(doc(db, USERS_DB, id), { permissions });
    }
    const lastLoginDate = data.lastLogin?.toDate?.() ?? null;
    const lastLogin = lastLoginDate ? lastLoginDate.getTime() : null; // store as ms for sorting
    list.push({ id, ...data, permissions, lastLogin, sessionCount: null, tournamentCount: null });
  }
  allUsers.value = list;

  // Fetch session and tournament counts for all users in parallel
  await Promise.all(list.map(async (user) => {
    if (!user.user_id) return;
    const [sessSnap, tourSnap] = await Promise.all([
      getCountFromServer(query(collection(db, SESSIONS_DB), where('user_id', '==', user.user_id))),
      getCountFromServer(query(collection(db, SAVED_TOURNAMENT_RESULTS_DB), where('createdBy', '==', user.user_id))),
    ]);
    user.sessionCount = sessSnap.data().count;
    user.tournamentCount = tourSnap.data().count;
  }));
  // Trigger reactivity — reassign to force computed refresh
  allUsers.value = [...list];
};

const setPermission = async (user, key, value) => {
  const userDocRef = doc(db, USERS_DB, user.id);
  if (key === 'admin') {
    await updateDoc(userDocRef, { 'permissions.admin': !!value });
    const u = allUsers.value.find(r => r.id === user.id);
    if (u && u.permissions) u.permissions.admin = !!value;
  } else {
    const newVal = value ? 'approved' : null;
    await updateDoc(userDocRef, { [`permissions.${key}`]: newVal });
    const u = allUsers.value.find(r => r.id === user.id);
    if (u && u.permissions) u.permissions[key] = newVal;
  }
};

const approve = async (user, permission) => {
  const userDocRef = doc(db, USERS_DB, user.id);
  await updateDoc(userDocRef, { [`permissions.${permission}`]: 'approved' });
  await fetchPendingUsers();
};

const requestDeleteUser = (user) => {
  userToDelete.value = user;
  deleteDialog.value = true;
};

const confirmDeleteUser = async () => {
  if (!userToDelete.value) return;

  const userId = userToDelete.value.user_id;

  try {
    const settingsQuery = query(collection(db, TOURNAMENT_SETTINGS_DB), where('ownerId', '==', userId));
    const settingsSnapshot = await getDocs(settingsQuery);

    const deletePromises = [];
    settingsSnapshot.forEach(settingDoc => {
      const tournamentId = settingDoc.id;
      deletePromises.push(deleteDoc(doc(db, TOURNAMENT_SETTINGS_DB, tournamentId)));
      deletePromises.push(deleteDoc(doc(db, TOURNAMENT_PLAYERS_DB, tournamentId)));
      deletePromises.push(deleteDoc(doc(db, TOURNAMENT_PAYOUTS_DB, tournamentId)));
      deletePromises.push(deleteDoc(doc(db, TOURNAMENT_TIMERS_DB, tournamentId)));
    });

    const sessionsQuery = query(collection(db, SESSIONS_DB), where('user_id', '==', userId));
    const sessionsSnapshot = await getDocs(sessionsQuery);
    sessionsSnapshot.forEach(sessionDoc => {
      deletePromises.push(deleteDoc(sessionDoc.ref));
    });

    deletePromises.push(deleteDoc(doc(db, USERS_DB, userToDelete.value.id)));
    await Promise.all(deletePromises);

  } catch (error) {
    console.error("Error deleting user and their data:", error);
  }

  await fetchAllUsers();
  deleteDialog.value = false;
  userToDelete.value = null;
};

const fetchOrphanSessions = async () => {
  if (allUsers.value.length === 0) {
    await fetchAllUsers();
  }
  const activeUserIds = new Set(
    allUsers.value
      .filter(u => u.permissions && (u.permissions.poker === 'approved' || u.permissions.tournament === 'approved'))
      .map(u => u.user_id)
  );
  const sessionsRef = collection(db, SESSIONS_DB);
  const snapshot = await getDocs(sessionsRef);
  const list = [];
  snapshot.forEach(snap => {
    const data = snap.data();
    if (!data.user_id || !activeUserIds.has(data.user_id)) {
      list.push({ id: snap.id, ...data });
    }
  });
  orphanSessions.value = list;
};

const requestDeleteSession = (session) => {
  sessionToDelete.value = session;
  sessionDeleteDialog.value = true;
};

const confirmDeleteSession = async () => {
  if (!sessionToDelete.value) return;
  try {
    await deleteDoc(doc(db, SESSIONS_DB, sessionToDelete.value.id));
    await fetchOrphanSessions();
  } catch (err) {
    console.error('Error deleting session:', err);
  }
  sessionDeleteDialog.value = false;
  sessionToDelete.value = null;
};

const fetchMissingUserIdSessions = async () => {
  missingUserIdLoading.value = true;
  if (allUsers.value.length === 0) await fetchAllUsers();
  try {
    const snapshot = await getDocs(collection(db, SESSIONS_DB));
    const list = [];
    snapshot.forEach(snap => {
      const data = snap.data();
      if (!data.user_id) list.push({ id: snap.id, ...data });
    });
    list.sort((a, b) => {
      const da = a.sessiondate?.toDate?.() ?? new Date(a.sessiondate ?? 0);
      const db2 = b.sessiondate?.toDate?.() ?? new Date(b.sessiondate ?? 0);
      return db2 - da;
    });
    missingUserIdSessions.value = list;
    sessionAssignments.value = {};
  } catch (err) {
    console.error('Error fetching missing user_id sessions:', err);
  }
  missingUserIdLoading.value = false;
};

const assignUserId = async (session) => {
  const userId = sessionAssignments.value[session.id];
  if (!userId) return;
  savingSessionId.value = session.id;
  try {
    await updateDoc(doc(db, SESSIONS_DB, session.id), { user_id: userId });
    missingUserIdSessions.value = missingUserIdSessions.value.filter(s => s.id !== session.id);
    const assignments = { ...sessionAssignments.value };
    delete assignments[session.id];
    sessionAssignments.value = assignments;
  } catch (err) {
    console.error('Error assigning user_id:', err);
  }
  savingSessionId.value = null;
};

function formatDate(ts) {
  if (!ts) return '';
  const d = ts instanceof Date ? ts : ts.toDate?.() ?? new Date(ts);
  return d.toLocaleDateString();
}

function formatLastLogin(ms) {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const fetchFeedback = async () => {
  try {
    const q = query(collection(db, FEEDBACK_DB), orderBy('submittedAt', 'desc'));
    const snap = await getDocs(q);
    allFeedback.value = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.error('Error fetching feedback:', e);
  }
};

const updateFeedbackStatus = async (item, newStatus) => {
  try {
    await updateDoc(doc(db, FEEDBACK_DB, item.id), { status: newStatus });
    item.status = newStatus;
  } catch (e) {
    console.error('Error updating feedback status:', e);
  }
};

function feedbackTypeColor(t) {
  if (t === 'Bug Report') return 'error';
  if (t === 'Feature Request') return 'primary';
  return 'secondary';
}

onMounted(fetchPendingUsers);
// Pre-load feedback count for the badge even before the tab is opened
onMounted(fetchFeedback);

watch(tab, (newTab) => {
  loadedTabs.value.add(newTab);
  if (newTab === 'active' && allUsers.value.length === 0) {
    fetchAllUsers();
  }
  if (newTab === 'orphan') {
    fetchOrphanSessions();
  }
  if (newTab === 'structures' && allUsers.value.length === 0) {
    fetchAllUsers();
  }
  if (newTab === 'feedback') {
    fetchFeedback();
  }
  if (newTab === 'missinguser') {
    fetchMissingUserIdSessions();
  }
});

function goToOrphanedUsers() {
  router.push({ name: 'OrphanedUsers' });
}
</script>
